import React, { Component } from "react";
import { StyleSheet, Modal, View, Text, TouchableOpacity, TextInput, Platform, ScrollView, ActivityIndicator } from "react-native";
import { MaterialCommunityIcons, MaterialIcons } from "@expo/vector-icons";
import colors from "../constants/colors";
import { formatStringNumber, getTotalSelected, sanitizeRecipeNumericInput, simpleAlert, validateDecimals } from "../utils";
import { isMobileWeb, isWideMobileWeb } from "../utils/mobileWeb";
import BarcodeScannerComponent from "./widgets/BarCodeScannerComponent";
import {
    exceedsRemainingLotAmount,
    findBlockingAllocation,
    formatAlreadyAllocatedShipmentAlertMessage,
    getAllocatedQuantity,
    getLotStockAmount,
    getMatchingAllocations,
    getRemainingLotAmount,
    isLotFullyAllocated,
    roundShipmentQty,
} from "../utils/shipmentAllocations";

const SELECTED_COL_WIDTHS = ['7%', '32%', '26%', '35%'];
const SELECTED_COL_WIDTHS_WITH_TYPE = ['7%', '24%', '16%', '22%', '31%'];
const STOCK_COL_WIDTHS = ['7%', '26%', '18%', '18%', '31%'];
const STOCK_COL_WIDTHS_WITH_TYPE = ['7%', '20%', '14%', '18%', '16%', '25%'];

const COMPACT_TYPE_LABELS = {
    finishedGoods: 'FG',
    workInProgress: 'WIP',
    rawMaterials: 'RM',
    packagingMaterials: 'PKG',
    'Finished Goods': 'FG',
    'Work In Progress': 'WIP',
    'Raw Materials': 'RM',
    'Packaging Materials': 'PKG',
};

function getTableLayout(showInventoryType, compact) {
    if (compact) {
        if (showInventoryType) {
            return {
                compact: true,
                selectedFullWidth: true,
                stockFullWidth: true,
                selectedWidths: ['7%', '36%', '9%', '20%', '28%'],
                stockWidths: STOCK_COL_WIDTHS_WITH_TYPE,
                selectedMinWidth: null,
                stockMinWidth: null,
                headerSize: 11,
                bodySize: 12,
                selectedHeaders: ['', 'Product', 'Type', 'Lot', 'Amt'],
                stockHeaders: ['', 'Product', 'Type', 'Lot', 'Amt', 'Date'],
            };
        }
        return {
            compact: true,
            selectedFullWidth: true,
            stockFullWidth: true,
            selectedWidths: ['7%', '42%', '22%', '29%'],
            stockWidths: STOCK_COL_WIDTHS,
            selectedMinWidth: null,
            stockMinWidth: null,
            headerSize: 11,
            bodySize: 12,
            selectedHeaders: ['', 'Product', 'Lot', 'Amt'],
            stockHeaders: ['', 'Product', 'Lot', 'Amt', 'Date'],
        };
    }
    return {
        compact: false,
        selectedFullWidth: false,
        stockFullWidth: false,
        selectedWidths: showInventoryType ? SELECTED_COL_WIDTHS_WITH_TYPE : SELECTED_COL_WIDTHS,
        stockWidths: showInventoryType ? STOCK_COL_WIDTHS_WITH_TYPE : STOCK_COL_WIDTHS,
        selectedMinWidth: null,
        stockMinWidth: null,
        headerSize: 14,
        bodySize: 14,
        selectedHeaders: showInventoryType
            ? ['', 'Product', 'Type', 'Lot Code', 'Amount']
            : ['', 'Product', 'Lot Code', 'Amount'],
        stockHeaders: showInventoryType
            ? ['', 'Product', 'Type', 'Lot Code', 'Amount', 'Prod. Date']
            : ['', 'Product', 'Lot Code', 'Amount', 'Prod. Date'],
    };
}

/**
 * Lot/item picker for isShipmentByLocation forms.
 * Options are location inventory rows shaped like LotsByLocation options, with an extra `item` (product name) field.
 */
class ItemsByLocationModal extends Component {
    constructor(props) {
        super(props);
        this._interval = null;
        const amount = props.amount != null && props.amount !== "" ? parseFloat(formatStringNumber(String(props.amount))) : undefined;
        this.state = {
            value: "",
            height: 30,
            filtered: props.options ? props.options : [],
            itemsSelected: this.setItemsSelected(props.itemsSelected, JSON.parse(JSON.stringify(props.options || [])), isNaN(amount) ? undefined : amount, props.units),
            scan: false,
            editQty: false,
            modalVisible: false,
            loading: props.loading || false,
        };
    }

    componentWillUnmount() {
        if (this._interval) clearInterval(this._interval);
    }

    componentWillReceiveProps(nextProps) {
        this.syncModalProps(nextProps, this.props);
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.options !== this.props.options
            || prevProps.loading !== this.props.loading
            || prevProps.itemsSelected !== this.props.itemsSelected
            || prevProps.amount !== this.props.amount
            || prevProps.units !== this.props.units
        ) {
            this.syncModalProps(this.props, prevProps);
        }
    }

    syncModalProps(nextProps, prevProps) {
        if (!prevProps) return;
        var shouldSyncSelected = nextProps.itemsSelected != prevProps.itemsSelected && nextProps.itemsSelected;
        var shouldSyncOptions = nextProps.options != prevProps.options;
        var shouldSyncLoading = nextProps.loading != prevProps.loading;

        if (!shouldSyncSelected && !shouldSyncOptions && !shouldSyncLoading) return;

        const amount = nextProps.amount != null && nextProps.amount !== "" ? parseFloat(formatStringNumber(String(nextProps.amount))) : undefined;
        var nextState = {};

        if (shouldSyncOptions) {
            nextState.filtered = nextProps.options || [];
            nextState.itemsSelected = this.setItemsSelected(
                nextProps.itemsSelected,
                JSON.parse(JSON.stringify(nextProps.options || [])),
                undefined,
                nextProps.units
            );
        } else if (shouldSyncSelected) {
            nextState.itemsSelected = this.setItemsSelected(
                nextProps.itemsSelected,
                JSON.parse(JSON.stringify(nextProps.options || [])),
                isNaN(amount) ? undefined : amount,
                nextProps.units
            );
        }

        if (shouldSyncLoading) {
            nextState.loading = nextProps.loading;
        }

        if (Object.keys(nextState).length) {
            this.setState(nextState);
        }
    }

    setInitialState() {
        this.setState({
            value: "",
            filtered: [],
            itemsSelected: [],
            editQty: false,
        });
    }

    setItemsSelected(itemsSelected, options, amount, units) {
        var newSelected = [];
        var numAmount = amount != null && amount !== "" ? parseFloat(formatStringNumber(String(amount))) : undefined;
        if (numAmount != null && isNaN(numAmount)) numAmount = undefined;
        if (typeof itemsSelected == "string") {
            newSelected = [{
                item: itemsSelected,
                lot: "",
                historial: [],
                minDate: "",
                production_date: "",
                totalAmount: numAmount != null ? (numAmount % 1 !== 0 && !validateDecimals(String(numAmount))) ? Number(numAmount.toFixed(3)) : numAmount : 0,
                editedAmount: numAmount != null ? numAmount : 0,
                isNew: true,
                units,
            }];
        } else if (itemsSelected?.length > 0 && itemsSelected[0] == "default_lot") {
            if (typeof options == "object" && options?.length > 0) {
                var selectedAmount = (numAmount != null && !isNaN(numAmount)) ? numAmount : 0;
                options.map((option) => {
                    if (option?.totalAmount > 0 && selectedAmount > 0) {
                        if (option?.historial) {
                            option.historial.reverse().map((batch) => {
                                if (batch?.amount > 0 && selectedAmount > 0) {
                                    if ((selectedAmount - batch?.amount) < 0) {
                                        batch.editedAmount = (selectedAmount % 1 !== 0 && !validateDecimals(String(selectedAmount))) ? Number(selectedAmount.toFixed(3)) : selectedAmount;
                                    }
                                    selectedAmount -= batch?.amount;
                                } else {
                                    batch.hide = true;
                                }
                                return batch;
                            });
                        }
                        newSelected.push(option);
                    }
                    return option;
                });
            } else {
                newSelected = [{
                    item: "",
                    lot: "",
                    historial: [],
                    minDate: "",
                    production_date: "",
                    totalAmount: numAmount != null ? (numAmount % 1 !== 0 && !validateDecimals(String(numAmount))) ? Number(numAmount.toFixed(3)) : numAmount : 0,
                    editedAmount: numAmount != null ? numAmount : 0,
                    isNew: true,
                    units,
                }];
            }
        } else if (itemsSelected?.length > 0) {
            newSelected = itemsSelected;
        }
        return newSelected;
    }

    findMatchingRowIndex(rows, option) {
        if (!rows?.length || !option) return -1;
        if (option.batchId) {
            var byBatchId = rows.findIndex((row) => row.batchId === option.batchId || row?.historial?.[0]?._id === option.batchId);
            if (byBatchId > -1) return byBatchId;
        }
        return rows.findIndex((row) => {
          if (row.lot != option.lot || row.item != option.item) return false;
          if (option.inventoryCategory && row.inventoryCategory) {
            return row.inventoryCategory === option.inventoryCategory;
          }
          return true;
        });
    }

    isCompactLayout() {
        return Platform.OS === 'web' && isMobileWeb() && !isWideMobileWeb();
    }

    colStyle(width, fullWidthRow) {
        if (typeof width === 'string' && width.endsWith('%')) {
            return { width, flexShrink: 1, minWidth: 0 };
        }
        if (this.isCompactLayout() && typeof width === 'number' && !fullWidthRow) {
            return { width, minWidth: width, maxWidth: width, flexShrink: 0, flexGrow: 0 };
        }
        return { width, flexShrink: fullWidthRow ? 1 : 0, minWidth: fullWidthRow ? 0 : undefined };
    }

    formatInventoryType(option) {
        var full = option?.inventoryTypeLabel || option?.inventoryType || "";
        if (!this.isCompactLayout()) return full;
        if (option?.inventoryCategory && COMPACT_TYPE_LABELS[option.inventoryCategory]) {
            return COMPACT_TYPE_LABELS[option.inventoryCategory];
        }
        return COMPACT_TYPE_LABELS[full] || full;
    }

    /** Selection UX for shipment/transfer by location; allocation alerts only for shipment. */
    isLocationForm() {
        const { isShipmentByLocation, isTransferByLocation } = this.props;
        return !!(isShipmentByLocation || isTransferByLocation);
    }

    usesShipmentAllocationAlerts() {
        const { isShipmentByLocation, isTransferByLocation } = this.props;
        return !!(isShipmentByLocation && !isTransferByLocation);
    }

    getShipmentAllocationOptions(option, batch) {
        const { currentSubmissionId } = this.props;
        const lotSource = option || batch;
        return {
            lot: lotSource?.lot || option?.lot || "",
            batchId: batch?.batchId || batch?._id || batch?.id || option?.batchId || option?._id || option?.id || null,
            itemName: option?.item || batch?.item || "",
            currentSubmissionId,
        };
    }

    getRemainingAmountForLot(option, batch) {
        const { alreadyAllocatedShipmentLots } = this.props;
        const stockSource = this.getOriginalStockSource(option, batch);
        return getRemainingLotAmount(
            stockSource,
            alreadyAllocatedShipmentLots,
            this.getShipmentAllocationOptions(option, batch)
        );
    }

    getOriginalStockSource(option, batch) {
        const options = this.props.options || [];
        const originalLot = (options.find((opt) => {
            if (!opt || !option) return false;
            if (
                opt.lot != null && option.lot != null && String(opt.lot) === String(option.lot)
                && (opt.item == null || option.item == null || String(opt.item) === String(option.item))
            ) return true;
            const optId = opt.batchId || opt._id || opt.id;
            const optionId = option.batchId || option._id || option.id;
            return !!(optId && optionId && optId === optionId);
        }) || option);
        if (!batch) {
            return {
                amount: originalLot?.amount,
                totalAmount: originalLot?.totalAmount,
                historial: originalLot?.historial,
            };
        }
        const historial = originalLot?.historial || [];
        const originalBatch = historial.find((entry) => {
            if (!entry) return false;
            const entryIds = [entry.batchId, entry._id, entry.id].filter(Boolean);
            const batchIds = [batch.batchId, batch._id, batch.id].filter(Boolean);
            return entryIds.some((id) => batchIds.indexOf(id) >= 0);
        }) || batch;
        return { amount: originalBatch.amount, totalAmount: originalBatch.totalAmount || originalBatch.amount };
    }

    isEmptyAmountInput(filledValue) {
        if (filledValue == null) return true;
        const text = String(filledValue).trim();
        if (text === "" || text === "." || text === "-" || text === "-.") return true;
        const filled = parseFloat(text);
        return Number.isNaN(filled) || !(filled > 0);
    }

    filledAmountExceedsExistentStock(filledValue, option, batch) {
        if (this.isEmptyAmountInput(filledValue)) return false;
        const filled = parseFloat(String(filledValue).trim());
        const existent = getLotStockAmount(this.getOriginalStockSource(option, batch));
        if (!(existent > 0)) return false;
        const allocated = getAllocatedQuantity(
            this.props.alreadyAllocatedShipmentLots,
            this.getShipmentAllocationOptions(option, batch)
        );
        return roundShipmentQty(allocated + filled) > roundShipmentQty(existent);
    }

    isShipmentLotAllocated(option, batch) {
        const { alreadyAllocatedShipmentLots } = this.props;
        if (!this.usesShipmentAllocationAlerts() || !alreadyAllocatedShipmentLots?.length || !option) return false;
        const lotNorm = (option.lot || batch?.lot || "").toString().trim().toLowerCase();
        const batchId = batch?.batchId || batch?._id || batch?.id || option.batchId || option._id || option.id || null;
        if ((!lotNorm || lotNorm === "default_lot") && !batchId) return false;
        const stockSource = this.getOriginalStockSource(option, batch);
        return isLotFullyAllocated(
            stockSource,
            alreadyAllocatedShipmentLots,
            this.getShipmentAllocationOptions(option, batch)
        );
    }

    findAllocatedShipmentLot(option, batch) {
        const { alreadyAllocatedShipmentLots } = this.props;
        if (!alreadyAllocatedShipmentLots?.length) return null;
        return findBlockingAllocation(
            alreadyAllocatedShipmentLots,
            this.getShipmentAllocationOptions(option, batch)
        );
    }

    showAlreadyAllocatedShipmentAlert(option, batch) {
        const { openAlert, alreadyAllocatedShipmentLots } = this.props;
        const itemName = option?.item || batch?.item || "";
        const matches = getMatchingAllocations(
            alreadyAllocatedShipmentLots,
            this.getShipmentAllocationOptions(option, batch)
        );
        const fallback = this.findAllocatedShipmentLot(option, batch) || {
            item: itemName,
            lot: option?.lot || batch?.lot || "",
            quantity: option?.editedAmount != null ? option.editedAmount : (batch?.amount != null ? batch.amount : option?.amount),
        };
        const allocatedList = matches.length ? matches : [fallback];
        const message = formatAlreadyAllocatedShipmentAlertMessage(allocatedList, { itemName });
        const present = () => {
            simpleAlert(
                "Inventory Already Allocated",
                (message || "").replace(/\n/g, "<br>"),
                "OK",
                null,
                null,
                () => { if (openAlert) openAlert(false); }
            );
        };
        if (openAlert) {
            openAlert(true);
            setTimeout(present, Platform.OS === "web" ? 120 : 50);
        } else {
            present();
        }
    }

    selectBatch(option, batch, isNew, batchIndex) {
        const { itemsSelected } = this.state;
        const { readOnly } = this.props;
        const isLocationForm = this.isLocationForm();
        if (readOnly) return;
        var foundIdx = this.findMatchingRowIndex(itemsSelected, option);
        var foundItem = foundIdx > -1 ? itemsSelected[foundIdx] : null;
        if (foundItem && !isNew && !isLocationForm) {
            return;
        }
        // Partial allocations: allow when free stock remains; alert only when none left.
        if (!isNew && this.usesShipmentAllocationAlerts()) {
            const remaining = this.getRemainingAmountForLot(option, batch);
            if (!(remaining > 0)) {
                this.showAlreadyAllocatedShipmentAlert(option, batch);
                return;
            }
        }
        var newSelected = itemsSelected && typeof itemsSelected == "object" ? JSON.parse(JSON.stringify(itemsSelected)) : [];
        if (isNew) {
            newSelected.push({
                item: "",
                historial: [],
                lot: "",
                minDate: "",
                units: option?.units,
                inventoryType: option?.inventoryType || "",
                inventoryCategory: option?.inventoryCategory || "",
                production_date: "",
                totalAmount: 0,
                editedAmount: 0,
                isNew: true,
            });
        } else {
            var optionToAdd = JSON.parse(JSON.stringify(option));
            if (foundItem && isLocationForm) {
                newSelected[foundIdx] = this.editSelectedBatch(foundItem, true, batchIndex);
            } else {
                if (isLocationForm) {
                    optionToAdd = this.editSelectedBatch(optionToAdd, false, batchIndex);
                } else {
                    optionToAdd = this.editSelectedBatch(optionToAdd, false, -1);
                }
                newSelected.push(optionToAdd);
            }
        }
        this.setState({ itemsSelected: newSelected, editQty: true }, () => {
            this.props.onSelect(newSelected, batch?.location);
        });
    }

    editItemCode(idx, type, value, subIdx) {
        const { itemsSelected } = this.state;
        const { alreadyAllocatedShipmentLots } = this.props;
        var newSelected = JSON.parse(JSON.stringify(itemsSelected));
        if (!validateDecimals(value == null ? "" : String(value)) && type == "amount") return;

        if (type == "item") {
            newSelected[idx].item = value;
        } else if (type == "lotcode") {
            newSelected[idx].lot = value;
        } else {
            const clearingAmount = this.isEmptyAmountInput(value);
            if (this.usesShipmentAllocationAlerts() && alreadyAllocatedShipmentLots?.length && !clearingAmount) {
                const lotOption = newSelected[idx];
                const batch = subIdx != null ? lotOption?.historial?.[subIdx] : null;
                if (this.filledAmountExceedsExistentStock(value, lotOption, batch)) {
                    this.showAlreadyAllocatedShipmentAlert(lotOption, batch);
                    return;
                }
            }
            if (subIdx != null) {
                newSelected[idx].historial[subIdx].editedAmount = value;
            } else {
                newSelected[idx].editedAmount = value;
            }
            if (clearingAmount) {
                this.setState({ itemsSelected: newSelected, editQty: true });
                return;
            }
        }
        this.setState({ itemsSelected: newSelected, editQty: true }, () => {
            this.props.onSelect(newSelected, null);
        });
    }

    quitBatch(option, isNew, batchIndex) {
        const { itemsSelected } = this.state;
        const { readOnly } = this.props;
        const isLocationForm = this.isLocationForm();
        if (readOnly) return;
        var foundIdx = -1;
        if (isNew) foundIdx = itemsSelected.findIndex((row) => row.isNew);
        else foundIdx = this.findMatchingRowIndex(itemsSelected, option);
        var newSelected = JSON.parse(JSON.stringify(itemsSelected));
        if (foundIdx > -1) {
            if (isNew || !isLocationForm) {
                newSelected.splice(foundIdx, 1);
            } else {
                var row = newSelected[foundIdx];
                var quitRow = true;
                if (row) {
                    row.historial.map((batch, idx) => {
                        if (idx != batchIndex && !batch.hide) quitRow = false;
                        return batch;
                    });
                }
                if (quitRow) newSelected.splice(foundIdx, 1);
                else {
                    var batch = newSelected[foundIdx].historial[batchIndex];
                    batch.hide = true;
                    delete batch.editedAmount;
                    newSelected[foundIdx].historial[batchIndex] = batch;
                }
            }
        }
        this.setState({ itemsSelected: newSelected, editQty: true }, () => {
            this.props.onSelect(newSelected, null);
        });
    }

    handleBarCodeScanned(val) {
        const { itemsSelected } = this.state;
        const { options } = this.props;
        var foundInSelected = null;
        if (itemsSelected && typeof itemsSelected == "object") {
            foundInSelected = itemsSelected.find((row) => row.lot == val || row?.historial?.[0]?.lot == val);
        }
        this.showScanner(false);
        if (foundInSelected) {
            simpleAlert("", "This lot code was already selected", "Accept", "", "", null);
            return;
        }
        var foundLot = options.find((row) => row.lot == val || row?.historial?.[0]?.lot == val);
        if (foundLot && this.isShipmentLotAllocated(foundLot)) {
            this.showAlreadyAllocatedShipmentAlert(foundLot);
            return;
        }
        var newSelected = itemsSelected && typeof itemsSelected == "object" ? JSON.parse(JSON.stringify(itemsSelected)) : [];
        if (foundLot) {
            foundLot = this.editSelectedBatch(JSON.parse(JSON.stringify(foundLot)), false, -1);
            newSelected.push(foundLot);
        } else {
            newSelected.push({
                item: "",
                lot: val,
                historial: [],
                minDate: "",
                production_date: "",
                totalAmount: 0,
                editedAmount: 0,
                isNew: true,
            });
        }
        this.setState({ itemsSelected: newSelected, editQty: true }, () => {
            this.props.onSelect(newSelected, null);
        });
    }

    search(value) {
        const { options } = this.props;
        if (this._interval) clearInterval(this._interval);
        if (value) {
            var searchVal = value.toLowerCase().trim();
            var filtered = options.filter((option) => {
                var itemName = (option?.item || "").toLowerCase();
                var lot = (option?.lot || "").toLowerCase();
                var productCode = (option?.productCode || "").toLowerCase();
                var inventoryType = (option?.inventoryType || "").toLowerCase();
                var inventoryTypeLabel = (option?.inventoryTypeLabel || "").toLowerCase();
                return itemName.includes(searchVal) || lot.includes(searchVal) || productCode.includes(searchVal) || inventoryType.includes(searchVal) || inventoryTypeLabel.includes(searchVal);
            });
            this._interval = setInterval(() => {
                clearInterval(this._interval);
                var found = filtered.find((option) => option.lot && option.lot.toLowerCase().trim() == searchVal);
                if (found) {
                    if (this.isShipmentLotAllocated(found)) {
                        this.showAlreadyAllocatedShipmentAlert(found);
                        this.setState({ filtered: this.props.options });
                        return;
                    }
                    found.historial.map((batch, idx) => {
                        if (batch?.hide) return;
                        if (this.usesShipmentAllocationAlerts()) {
                            if (this.getRemainingAmountForLot(found, batch) > 0) {
                                this.selectBatch(found, batch, false, idx);
                            }
                        } else {
                            this.selectBatch(found, batch, false, idx);
                        }
                        return batch;
                    });
                    this.setState({ filtered: this.props.options });
                }
            }, 500);
            this.setState({ filtered });
        } else {
            this.setState({ filtered: options });
        }
    }

    renderInput(value, onchange, type, width) {
        const { readOnly } = this.props;
        const handleChange = (type === "numeric")
            ? (text) => onchange(sanitizeRecipeNumericInput(text))
            : onchange;
        const kb = type === "numeric" ? "decimal-pad" : "default";
        return (
            <TextInput
                value={value}
                editable={!readOnly}
                onChangeText={handleChange}
                keyboardType={kb}
                style={{ width: width, textAlign: 'center', borderWidth: 1, borderRadius: 10, borderColor: colors.orange, color: colors.orange }}
            />
        );
    }

    showScanner(val) {
        this.setState({ scan: val });
    }

    renderAddItemBtn(units) {
        const { readOnly } = this.props;
        if (readOnly) return null;
        return (
            <TouchableOpacity
                style={{ ...styles.tableHeader, borderBottomWidth: 0 }}
                onPress={() => this.selectBatch({ item: "", lot: "", units }, null, true)}
            >
                <Text style={{ ...styles.tableBodyTxt, width: '10%', textAlign: 'left' }}>
                    <MaterialCommunityIcons name="plus-circle-outline" style={{ ...styles.rowIcon, color: colors.add }} />
                </Text>
            </TouchableOpacity>
        );
    }

    renderAmountAlert(modalVisible, requiredAmount, totalAmount, units) {
        return (
            <Modal
                animationType="slide"
                transparent={true}
                visible={modalVisible}
                onRequestClose={() => this.setState({ modalVisible: false })}
            >
                <View style={styles.centeredView}>
                    <View style={styles.modalView}>
                        <Text style={styles.modalText}><MaterialIcons name="warning-amber" size={50} style={{ color: colors.quit }} /></Text>
                        <Text style={styles.modalText}>The amount entered does not match the required quantity</Text>
                        <Text style={{ ...styles.modalText, fontWeight: '700' }}>Required: {parseFloat(requiredAmount)} {units}</Text>
                        <Text style={{ ...styles.modalText, fontWeight: '700' }}>Entered: {parseFloat(totalAmount)} {units}</Text>
                        <Text style={styles.modalText}>This will reduce inventory by the entered amount.</Text>
                        <Text style={styles.modalText}>Make sure this change is intentional before proceeding.</Text>
                        <TouchableOpacity style={styles.modalBtn} onPress={() => this.setState({ modalVisible: false })}>
                            <Text style={{ ...styles.modalText, fontWeight: '700', marginBottom: 0, color: 'white' }}>Got it</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </Modal>
        );
    }

    editSelectedBatch(option, found, batchIndex) {
        const { itemsSelected } = this.state;
        const { requiredAmount } = this.props;
        const useAllocation = this.usesShipmentAllocationAlerts();
        var amountSelected = parseFloat(getTotalSelected(itemsSelected));
        var quantity = parseFloat(requiredAmount) - amountSelected;
        if (useAllocation) {
            const remaining = this.getRemainingAmountForLot(option);
            if (remaining >= 0 && quantity > remaining) quantity = remaining;
        }

        if (quantity > 0 || found) {
            if (option?.historial) {
                option.historial.map((batch, index) => {
                    if (found) {
                        if (index == batchIndex) batch.hide = false;
                    } else {
                        if (quantity > 0 && (batchIndex == index && batchIndex >= 0)) {
                            var maxTake = parseFloat(batch?.amount) || 0;
                            if (useAllocation) {
                                const remainingForBatch = this.getRemainingAmountForLot(option, batch);
                                if (remainingForBatch >= 0 && maxTake > remainingForBatch) maxTake = remainingForBatch;
                            }
                            var rest = maxTake - quantity;
                            if (rest >= 0) {
                                batch.editedAmount = (quantity % 1 > 0 && !validateDecimals(quantity.toString())) ? quantity.toFixed(3) : quantity;
                                quantity = 0;
                            } else {
                                quantity = rest * -1;
                                batch.editedAmount = (maxTake % 1 > 0 && !validateDecimals(maxTake.toString())) ? parseFloat(maxTake).toFixed(3) : maxTake;
                            }
                        } else {
                            batch.hide = true;
                        }
                    }
                    return batch;
                });
            }
        } else if (batchIndex >= 0) {
            option.historial.map((batch, index) => {
                if (index == batchIndex) {
                    batch.hide = false;
                    if (useAllocation) {
                        var maxTake = parseFloat(batch?.amount) || 0;
                        const remainingForBatch = this.getRemainingAmountForLot(option, batch);
                        if (remainingForBatch >= 0 && maxTake > remainingForBatch) maxTake = remainingForBatch;
                        if (batch.editedAmount == null || batch.editedAmount === "") {
                            batch.editedAmount = (maxTake % 1 > 0 && !validateDecimals(maxTake.toString())) ? parseFloat(maxTake).toFixed(3) : maxTake;
                        } else if (exceedsRemainingLotAmount(batch.editedAmount, this.getOriginalStockSource(option, batch), this.props.alreadyAllocatedShipmentLots, this.getShipmentAllocationOptions(option, batch))) {
                            batch.editedAmount = (maxTake % 1 > 0 && !validateDecimals(maxTake.toString())) ? parseFloat(maxTake).toFixed(3) : maxTake;
                        }
                    }
                } else batch.hide = true;
                return batch;
            });
        }
        return option;
    }

    formatProductionDate(batch) {
        if (!batch?.production_date) return "";
        var date = String(batch.production_date);
        if (this.isCompactLayout()) {
            return date.split(/[ T]/)[0];
        }
        return date;
    }

    renderColText(text, width, style, bodySize, fullWidthRow) {
        var compact = this.isCompactLayout();
        return (
            <Text
                style={{
                    ...styles.tableBodyTxt,
                    ...this.colStyle(width, fullWidthRow),
                    fontSize: bodySize || (compact ? 12 : 14),
                    ...style,
                }}
                numberOfLines={fullWidthRow ? 3 : (compact ? 4 : 2)}
            >
                {text}
            </Text>
        );
    }

    render() {
        const { options, onBack, name, placeholder, units, requiredAmount, locationLabel, readOnly, loading, showInventoryType, complete_date } = this.props;
        const { value, filtered, itemsSelected, scan, editQty, modalVisible } = this.state;
        const layout = getTableLayout(showInventoryType, this.isCompactLayout());
        const selectedColWidths = layout.selectedWidths;
        const stockColWidths = layout.stockWidths;
        const selectedHeaders = layout.selectedHeaders;
        const stockHeaders = layout.stockHeaders;
        var itemsUnits = units;
        const showMobileScan = Platform.OS !== "web" || isMobileWeb();
        const compact = layout.compact;
        const rowIconSize = compact ? 24 : 30;
        const hideSelectionSection = !!(readOnly || complete_date);

        return (
            <Modal animationType="slide" transparent={false} visible={true} backdropColor={'white'}>
                {scan && showMobileScan && (
                    <BarcodeScannerComponent
                        onBarCodeScanned={(e) => this.handleBarCodeScanned(e)}
                        closeScanner={() => this.showScanner(false)}
                        style={styles.absoluteFillObject}
                    />
                )}
                <View style={{ backgroundColor: colors.gray, height: '100%' }}>
                    <View style={styles.cardContainer}>
                        <View style={{ ...styles.headerContainer, borderRadius: 20 }}>
                            <TouchableOpacity style={styles.btnBack} onPress={() => {
                                onBack(editQty, getTotalSelected(itemsSelected), (parseFloat(getTotalSelected(itemsSelected)) < parseFloat(requiredAmount) || parseFloat(getTotalSelected(itemsSelected)) > parseFloat(requiredAmount)));
                                setTimeout(() => this.setInitialState(), 200);
                            }}>
                                <Text style={styles.btnBackIcon}>
                                    <MaterialIcons name="arrow-back-ios" size={25} />
                                </Text>
                            </TouchableOpacity>
                            <Text style={{ ...styles.headerTxt, flex: requiredAmount ? 3 : 6 }}>
                                Location: {locationLabel}
                            </Text>
                            {requiredAmount ? (
                                <Text style={styles.headerTxt}>
                                    Required Amount: {requiredAmount} {units}
                                </Text>
                            ) : null}
                        </View>
                        {/* {locationLabel ? (
                            <Text style={styles.locationLabel}>Location: {locationLabel}</Text>
                        ) : null} */}
                        <View style={{flex: hideSelectionSection ? 'none' : 3, ...styles.mainContent}}>
                            <View style={styles.selectedSection}>
                                {itemsSelected && typeof itemsSelected == "object" ? (
                                    <View style={styles.selectedTable}>
                                        <View style={styles.tableFullWidth}>
                                        <View style={{ ...styles.tableHeader, borderBottomWidth: 1, borderBottomColor: colors.orange, alignItems: 'center' }}>
                                            {selectedHeaders.map((header, idx) => (
                                                <Text key={idx} style={{ ...styles.tableHeaderTxt, ...this.colStyle(selectedColWidths[idx], layout.selectedFullWidth), fontSize: layout.headerSize }} numberOfLines={1}>
                                                    {header}
                                                </Text>
                                            ))}
                                        </View>
                                        {itemsSelected.length ? (
                                            <ScrollView style={styles.selectedList} nestedScrollEnabled>
                                                {itemsSelected.map((option, idxOpt) => {
                                                    if (!option?.isNew && option?.historial?.length > 0) {
                                                        return option.historial.map((batch, idx) => {
                                                            if (batch?.hide) return null;
                                                            if (batch?.units && !itemsUnits) itemsUnits = batch?.units;
                                                            return (
                                                                <View key={`${idxOpt}-${idx}`} style={{ ...styles.tableHeader, alignItems: 'flex-start' }}>
                                                                    <View style={{ ...styles.tableBodyTxt, ...this.colStyle(selectedColWidths[0], layout.selectedFullWidth), textAlign: 'left', paddingTop: 5 }}>
                                                                        {!readOnly && (
                                                                            <TouchableOpacity onPress={() => this.quitBatch(option, false, idx)}>
                                                                                <MaterialIcons name="highlight-remove" size={rowIconSize} color={colors.quit} />
                                                                            </TouchableOpacity>
                                                                        )}
                                                                    </View>
                                                                    {this.renderColText(option.item || "", selectedColWidths[1], { textAlign: 'left' }, layout.bodySize, layout.selectedFullWidth)}
                                                                    {showInventoryType ? this.renderColText(this.formatInventoryType(option), selectedColWidths[2], { textAlign: 'center' }, layout.bodySize, layout.selectedFullWidth) : null}
                                                                    {this.renderColText(option.lot && typeof option.lot == "string" ? option.lot : "No Lot", selectedColWidths[showInventoryType ? 3 : 2], undefined, layout.bodySize, layout.selectedFullWidth)}
                                                                    <View style={{ ...styles.tableBodyTxt, ...this.colStyle(selectedColWidths[showInventoryType ? 4 : 3], layout.selectedFullWidth), flexDirection: compact ? 'column' : 'row', alignItems: 'center' }}>
                                                                        {readOnly ? (
                                                                            <Text style={{ textAlign: 'center' }}>
                                                                                {(batch?.editedAmount != null && batch?.editedAmount !== "") ? batch.editedAmount : ((batch?.amount % 1 > 0 && !validateDecimals(batch.amount.toString())) ? batch.amount.toFixed(3) : batch.amount)} {batch?.units || itemsUnits}
                                                                            </Text>
                                                                        ) : (
                                                                            <Text style={{ textAlign: 'center' }}>
                                                                                {this.renderInput(
                                                                                    batch?.editedAmount || batch?.editedAmount == "" ? batch.editedAmount : ((batch?.amount % 1 > 0 && !validateDecimals(batch.amount.toString())) ? batch.amount.toFixed(3) : batch.amount),
                                                                                    (val) => this.editItemCode(idxOpt, "amount", val, idx),
                                                                                    "numeric",
                                                                                    layout.selectedFullWidth ? "100%" : (compact ? "85%" : "70%")
                                                                                )}
                                                                                {!layout.selectedFullWidth ? (
                                                                                    <Text style={{ fontSize: layout.bodySize, marginTop: compact ? 2 : 0 }}>{batch?.units || itemsUnits}</Text>
                                                                                ) : (
                                                                                    <Text style={{ fontSize: 10, marginTop: 2 }} numberOfLines={1}>{batch?.units || itemsUnits}</Text>
                                                                                )}
                                                                            </Text>
                                                                        )}
                                                                    </View>
                                                                </View>
                                                            );
                                                        });
                                                    } else if (option?.isNew) {
                                                        return (
                                                            <View key={idxOpt} style={{ ...styles.tableHeader, alignItems: 'flex-start' }}>
                                                                <View style={{ ...styles.tableBodyTxt, ...this.colStyle(selectedColWidths[0], layout.selectedFullWidth), textAlign: 'left', paddingTop: 5 }}>
                                                                    {!readOnly && (
                                                                        <TouchableOpacity onPress={() => this.quitBatch(option, true, -1)}>
                                                                            <MaterialIcons name="highlight-remove" size={rowIconSize} color={colors.quit} />
                                                                        </TouchableOpacity>
                                                                    )}
                                                                </View>
                                                                <Text style={{ ...styles.tableBodyTxt, ...this.colStyle(selectedColWidths[1], layout.selectedFullWidth), paddingTop: 10 }}>
                                                                    {readOnly ? option?.item : this.renderInput(option?.item, (val) => this.editItemCode(idxOpt, "item", val), "default", "100%")}
                                                                </Text>
                                                                {showInventoryType ? (
                                                                    <Text style={{ ...styles.tableBodyTxt, ...this.colStyle(selectedColWidths[2], layout.selectedFullWidth), paddingTop: 10, fontSize: layout.bodySize }}>
                                                                        {this.formatInventoryType(option)}
                                                                    </Text>
                                                                ) : null}
                                                                <Text style={{ ...styles.tableBodyTxt, ...this.colStyle(selectedColWidths[showInventoryType ? 3 : 2], layout.selectedFullWidth), paddingTop: 10 }}>
                                                                    {readOnly ? option?.lot : this.renderInput(option?.lot, (val) => this.editItemCode(idxOpt, "lotcode", val), "default", "100%")}
                                                                </Text>
                                                                <View style={{ ...styles.tableBodyTxt, ...this.colStyle(selectedColWidths[showInventoryType ? 4 : 3], layout.selectedFullWidth), flexDirection: compact ? 'column' : 'row', alignItems: 'center' }}>
                                                                    {readOnly ? (
                                                                        <Text>{option?.editedAmount} {option?.units}</Text>
                                                                    ) : (
                                                                        <Text style={{ textAlign: 'center' }}>
                                                                            {this.renderInput(option?.editedAmount, (val) => this.editItemCode(idxOpt, "amount", val, null), "numeric", layout.selectedFullWidth ? "100%" : (compact ? "85%" : "70%"))}
                                                                            <Text style={{ fontSize: layout.selectedFullWidth ? 10 : layout.bodySize, marginTop: 2 }} numberOfLines={1}>{option?.units}</Text>
                                                                        </Text>
                                                                    )}
                                                                </View>
                                                            </View>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                                {this.renderAddItemBtn(units)}
                                            </ScrollView>
                                        ) : this.renderAddItemBtn(units)}
                                        </View>
                                    </View>
                                ) : this.renderAddItemBtn(units)}
                                <View style={styles.totalRow}>
                                    <View style={{ flexDirection: "row", justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap" }}>
                                        {requiredAmount && (parseFloat(getTotalSelected(itemsSelected)) < parseFloat(requiredAmount) || parseFloat(getTotalSelected(itemsSelected)) > parseFloat(requiredAmount)) && (
                                            <TouchableOpacity onPress={() => this.setState({ modalVisible: true })} style={styles.warningContainer}>
                                                <MaterialIcons name="warning-amber" size={25} style={styles.warningIcon} />
                                                <Text style={styles.warningTxt}>The Amount does not match</Text>
                                            </TouchableOpacity>
                                        )}
                                        <Text style={{ textAlign: "right", fontSize: 16, fontWeight: "700", marginLeft: 8 }}>
                                            Total: {getTotalSelected(itemsSelected)} {itemsUnits}
                                        </Text>
                                    </View>
                                </View>
                            </View>
                        </View>
                        {!hideSelectionSection && (
                            <View style={styles.stockSection}>
                                {/* <View style={styles.stockHeaderRow}>
                                    <Text style={[styles.title, styles.stockHeaderTitle]} numberOfLines={1}>Available Stock at Location</Text>
                                    {showMobileScan && (
                                        <TouchableOpacity onPress={() => this.showScanner(true)} style={styles.scanFab}>
                                            <MaterialIcons name="qr-code-scanner" color="#fff" size={26} />
                                        </TouchableOpacity>
                                    )}
                                </View> */}
                                <View style={{ ...styles.searchView, borderColor: colors.orange }}>
                                    <Text style={{ ...styles.searchIcon, borderColor: colors.orange }}>
                                        <MaterialIcons name="search" size={25} />
                                    </Text>
                                    <TextInput
                                        value={value}
                                        onContentSizeChange={(event) => {
                                            if (event.nativeEvent.contentSize.height == 0) {
                                                this.setState({ height: 30 });
                                            } else {
                                                setTimeout(() => {
                                                    this.setState({ height: event.nativeEvent.contentSize.height });
                                                }, 5);
                                            }
                                        }}
                                        style={{ ...styles.input, color: colors.orange, height: this.state.height }}
                                        multiline={true}
                                        placeholder={placeholder || "Search by product or lot code"}
                                        placeholderTextColor={colors.orange}
                                        onChangeText={(val) => {
                                            this.setState({ value: val });
                                            this.search(val);
                                        }}
                                    />
                                </View>
                                <View style={styles.stockTable}>
                                    <ScrollView
                                        horizontal={compact && !layout.stockFullWidth}
                                        nestedScrollEnabled
                                        showsHorizontalScrollIndicator={compact && !layout.stockFullWidth}
                                        style={compact && !layout.stockFullWidth ? styles.horizontalTableScroll : undefined}
                                        contentContainerStyle={layout.stockFullWidth ? styles.tableFullWidth : undefined}
                                    >
                                        <View style={compact && !layout.stockFullWidth ? { minWidth: layout.stockMinWidth, flex: 1 } : styles.tableFullWidth}>
                                    <View style={{ ...styles.tableHeader, borderBottomWidth: 1, borderBottomColor: colors.orange, alignItems: 'center' }}>
                                        {stockHeaders.map((header, idx) => (
                                            <Text key={idx} style={{ ...styles.tableHeaderTxt, ...this.colStyle(stockColWidths[idx], layout.stockFullWidth), fontSize: layout.headerSize }} numberOfLines={1}>
                                                {header}
                                            </Text>
                                        ))}
                                    </View>
                                    {loading ? (
                                        <View style={{ padding: 20, alignItems: 'center' }}>
                                            <ActivityIndicator size="large" color={colors.orange} />
                                            <Text style={{ marginTop: 10 }}>Loading items at location...</Text>
                                        </View>
                                    ) : filtered.length ? (
                                        <ScrollView style={styles.stockList} nestedScrollEnabled>
                                            {filtered.map((option, optIdx) => {
                                                if (option?.historial) {
                                                    return option.historial.map((batch, idx) => (
                                                        <TouchableOpacity key={option.batchId || `${optIdx}-${idx}`} style={{ ...styles.tableHeader, alignItems: 'flex-start' }} onPress={() => this.selectBatch(option, batch, false, idx)}>
                                                            <View style={{ ...styles.tableBodyTxt, ...this.colStyle(stockColWidths[0], layout.stockFullWidth), textAlign: 'left', paddingTop: 5 }}>
                                                                <MaterialCommunityIcons name="plus-circle-outline" size={rowIconSize} color={colors.add} />
                                                            </View>
                                                            {this.renderColText(option.item || "", stockColWidths[1], { textAlign: 'left' }, layout.bodySize, layout.stockFullWidth)}
                                                            {showInventoryType ? this.renderColText(this.formatInventoryType(option), stockColWidths[2], { textAlign: 'center' }, layout.bodySize, layout.stockFullWidth) : null}
                                                            {this.renderColText(option.lot && typeof option.lot == "string" ? option.lot : "No Lot", stockColWidths[showInventoryType ? 3 : 2], undefined, layout.bodySize, layout.stockFullWidth)}
                                                            <View style={{ ...styles.tableBodyTxt, ...this.colStyle(stockColWidths[showInventoryType ? 4 : 3], layout.stockFullWidth), flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                                                <Text style={{ fontSize: layout.bodySize }}>
                                                                    {(batch?.amount % 1 > 0 && !validateDecimals(batch.amount.toString())) ? batch.amount.toFixed(3) : batch.amount}
                                                                </Text>
                                                                <Text style={{ fontSize: layout.bodySize }}>{batch?.units}</Text>
                                                            </View>
                                                            {this.renderColText(this.formatProductionDate(batch), stockColWidths[showInventoryType ? 5 : 4], { fontSize: layout.bodySize }, layout.bodySize, layout.stockFullWidth)}
                                                        </TouchableOpacity>
                                                    ));
                                                }
                                                return null;
                                            })}
                                        </ScrollView>
                                    ) : (
                                        <View><Text style={{ textAlign: 'center', padding: 10 }}>Sorry, no items were found at this location.</Text></View>
                                    )}
                                        </View>
                                    </ScrollView>
                                </View>
                            </View>
                        )}
                    </View>
                </View>
                {requiredAmount ? this.renderAmountAlert(
                    modalVisible,
                    (parseFloat(requiredAmount) % 1 > 0 && !validateDecimals(requiredAmount.toString())) ? parseFloat(requiredAmount).toFixed(3) : parseFloat(requiredAmount),
                    getTotalSelected(itemsSelected),
                    itemsUnits
                ) : null}
            </Modal>
        );
    }
}

export default ItemsByLocationModal;

const styles = StyleSheet.create({
    headerContainer: {
        flexDirection: 'row',
        backgroundColor: 'white',
        paddingTop: Platform.OS == "ios" ? 35 : 15,
        paddingBottom: 5,
    },
    btnBack: { flex: 1 },
    btnBackIcon: { padding: 10, color: colors.orange },
    headerTxt: {
        flex: 3,
        textAlign: 'center',
        padding: 10,
        fontSize: 18,
        fontWeight: '600',
        position: 'relative',
    },
    locationLabel: {
        textAlign: 'center',
        fontSize: 14,
        color: '#666',
        paddingBottom: 8,
        fontWeight: '500',
    },
    searchView: {
        backgroundColor: 'white',
        minHeight: 42,
        flexDirection: 'row',
        borderRadius: 15,
        paddingInline: 5,
        paddingVertical: 4,
        borderWidth: 1,
        marginInline: 10,
        flexShrink: 0,
    },
    input: {
        fontFamily: "Roboto",
        fontSize: 16,
        fontWeight: "400",
        padding: 10,
        flex: 9,
        marginLeft: 5,
    },
    searchIcon: {
        paddingTop: 5,
        color: colors.orange,
        borderRadius: 50,
        padding: 5,
        borderWidth: 1,
        textAlign: 'center',
    },
    tableHeader: {
        flexDirection: 'row',
        paddingTop: 4,
        paddingBottom: 4,
        borderBottomColor: 'gray',
        borderBottomWidth: 1,
        paddingLeft: isMobileWeb() ? 2 : 8,
        paddingRight: isMobileWeb() ? 2 : 8,
        flexShrink: 0,
        width: '100%',
    },
    tableHeaderTxt: {
        fontWeight: '600',
        fontSize: 14,
        textAlign: 'center',
    },
    tableBody: { flexDirection: 'column' },
    tableBodyTxt: {
        textAlign: 'center',
        paddingTop: 6,
        paddingBottom: 4,
        paddingHorizontal: 2,
    },
    title: { fontSize: 16, fontWeight: 'bold', padding: 4 },
    stockHeaderRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 4,
        paddingHorizontal: 5,
        flexShrink: 0,
    },
    stockHeaderTitle: { flex: 1, marginRight: 8, paddingVertical: 4 },
    scanFab: {
        width: 50,
        height: 50,
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 50,
        backgroundColor: colors.primary,
        flexShrink: 0,
    },
    rowIcon: { fontSize: 30 },
    unitsContainer: { flex: 2, justifyContent: 'flex-start', alignItems: 'center', padding: 3 },
    cardContainer: {
        flex: 1,
        height: '100%',
        margin: 'auto',
        backgroundColor: 'white',
        marginInline: isMobileWeb() ? 0 : '10%',
        marginTop: isMobileWeb() ? 0 : 10,
        marginBottom: isMobileWeb() ? 0 : 10,
        borderRadius: isMobileWeb() ? 0 : 20,
        padding: isMobileWeb() ? 6 : 10,
        flexDirection: 'column',
    },
    horizontalTableScroll: {
        flexGrow: 0,
        flexShrink: 1,
    },
    tableFullWidth: {
        width: '100%',
    },
    mainContent: {
        flexDirection: 'column',
        paddingHorizontal: isMobileWeb() ? 4 : 10,
        minHeight: 0,
    },
    selectedSection: {
        flex: 1,
        minHeight: 0,
        marginBottom: 4,
        flexDirection: 'column',
    },
    sectionTitle: {
        fontSize: 15,
        fontWeight: '700',
        color: colors.orange,
        paddingBottom: 4,
        paddingLeft: 2,
    },
    selectedTable: {
        flex: 1,
        minHeight: 0,
        width: '100%',
        backgroundColor: 'white',
        borderRadius: 10,
        borderWidth: 1,
        borderColor: '#e8e8e8',
        overflow: 'hidden',
    },
    selectedList: {
        flex: 1,
        minHeight: 0,
    },
    totalRow: {
        paddingVertical: 6,
        paddingHorizontal: 4,
        flexShrink: 0,
        borderTopWidth: 1,
        borderTopColor: '#e8e8e8',
        backgroundColor: '#fafafa',
    },
    stockSection: {
        flex: 3,
        minHeight: 0,
        paddingHorizontal: 0,
        paddingTop: 4,
    },
    stockTable: {
        flex: 1,
        minHeight: 0,
        width: '100%',
        backgroundColor: 'white',
        flexDirection: 'column',
        marginTop: 8,
        borderRadius: 10,
        marginLeft: 5,
        marginRight: 5,
        borderWidth: 1,
        borderColor: '#e8e8e8',
        overflow: 'hidden',
    },
    stockList: {
        flex: 1,
        minHeight: 0,
    },
    warningContainer: {
        backgroundColor: 'red',
        borderRadius: 20,
        paddingInline: 10,
        marginTop: 10,
        marginRight: 10,
        flexDirection: 'row',
    },
    warningIcon: { padding: 7, margin: 'auto', color: 'white' },
    warningTxt: { display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'white' },
    centeredView: { flex: 1, justifyContent: 'center', alignItems: 'center', marginTop: 22 },
    modalView: {
        margin: 20,
        backgroundColor: 'white',
        borderRadius: 20,
        padding: 35,
        alignItems: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 4,
        elevation: 5,
    },
    modalText: { marginBottom: 15, textAlign: 'center', fontSize: 18 },
    modalBtn: {
        backgroundColor: colors.orange,
        marginTop: 10,
        borderRadius: 20,
        padding: 15,
        paddingInline: 50,
    },
    absoluteFillObject: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 },
});
